import { createApi } from "@reduxjs/toolkit/query/react";
import { EventSource } from "eventsource";
import { createEntityAdapter } from "@reduxjs/toolkit";
import createBaseQuery from "./createBaseQuery";
export const cardsAdapter = createEntityAdapter({
    selectId: (card)=>card.card_id,
    sortComparer: (a, b)=>a.card_id.localeCompare(b.card_id)
});
export const systemErrorsAdapter = createEntityAdapter({
    selectId: (error)=>error.error_msg,
    sortComparer: (a, b)=>a.error_msg.localeCompare(b.error_msg)
});
export const networkMetricsAdapter = createEntityAdapter({
    selectId: (network)=>network.interface,
    sortComparer: (a, b)=>a.interface.localeCompare(b.interface)
});
export const entropyClientsAdapter = createEntityAdapter({
    selectId: (client)=>client.ip + client.user_agent,
    sortComparer: (a, b)=>(a.ip + a.user_agent).localeCompare(b.ip + b.user_agent)
});
export const tokensAdapter = createEntityAdapter({
    selectId: (token)=>token.pkcs11Path + token.serialNumber,
    sortComparer: (a, b)=>(a.name + a.label).localeCompare(b.name + b.label)
});
const initialStatusMsg = {
    cards: [],
    system_errors: [],
    system_metrics: {
        cpu: {},
        memory: {}
    },
    network_metrics: [],
    utilization: {
        consumption: {},
        requests: {},
        latency: {}
    },
    node_utilization: {},
    entropy_clients: [],
    entropy_clients_by_node: {}
};
// The 'any' type here is a workaround for a type inferrence issue in redux toolkit.
// See https://github.com/reduxjs/redux-toolkit/issues/4498
export function createStatusApi(baseUrl) {
    const statusApi = createApi({
        reducerPath: `statusAPI_${baseUrl}`,
        baseQuery: createBaseQuery(baseUrl),
        tagTypes: [
            "Hsms"
        ],
        endpoints: (build)=>({
                getSystemHealth: build.query({
                    query: ()=>"api/sys/health"
                }),
                getStatus: build.query({
                    queryFn: async ()=>{
                        // Return the initial empty status message with normalized cards and system errors
                        const normalizedCards = cardsAdapter.getInitialState();
                        const normalizedSystemErrors = systemErrorsAdapter.getInitialState();
                        const normalizedNetworkMetrics = networkMetricsAdapter.getInitialState();
                        const normalizedEntropyClients = entropyClientsAdapter.getInitialState();
                        const normalizedTokens = tokensAdapter.getInitialState();
                        return {
                            data: {
                                ...initialStatusMsg,
                                cards: normalizedCards,
                                system_errors: normalizedSystemErrors,
                                network_metrics: normalizedNetworkMetrics,
                                entropy_clients: normalizedEntropyClients,
                                error: "",
                                cpu_model: "",
                                softwareVersion: "",
                                tokens: normalizedTokens,
                                forbidden: true
                            }
                        };
                    },
                    async onCacheEntryAdded (_arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch, getState }) {
                        // Relative URL should send a request to NGINX, which
                        // should proxy the request to the appropriate luffy endpoint.
                        // helper function to create the EventSource with the current accessToken
                        const createEventSource = ()=>{
                            const accessToken = getState().auth.accessToken;
                            let url = `${baseUrl}/api/admin/system-status`;
                            let authHeader = {};
                            if (accessToken) {
                                authHeader = {
                                    Authorization: `Bearer ${accessToken}`
                                };
                            }
                            return new EventSource(url, {
                                fetch: (input, init)=>fetch(input, {
                                        ...init,
                                        headers: {
                                            ...init.headers,
                                            ...authHeader
                                        }
                                    })
                            });
                        };
                        let messageTimeout = null;
                        let reconnectTimeout = null;
                        let connectionOpen = false;
                        let retryCount = 0;
                        // Exponential backoff (10 tries): 2s → 4s → 8s → 16s → 30s → 30s (capped)
                        const MAX_RETRIES = 10;
                        const INITIAL_RETRY_DELAY = 2000;
                        const MAX_RETRY_DELAY = 30000;
                        const eventSourceRef = {
                            current: null
                        };
                        try {
                            await cacheDataLoaded;
                            const resetMessageTimeout = ()=>{
                                if (messageTimeout) clearTimeout(messageTimeout);
                                messageTimeout = setTimeout(()=>{
                                    // Only add the error if the connection is still open
                                    if (connectionOpen) {
                                        const error = {
                                            timestamp: Date.now(),
                                            error_msg: "No status messages received in over 30 seconds."
                                        };
                                        // Update the cache with the error
                                        updateCachedData((draft)=>{
                                            draft.system_errors = systemErrorsAdapter.upsertOne(draft.system_errors, error);
                                        });
                                    }
                                }, 30000); // 30 seconds
                            };
                            const listener = (event)=>{
                                const data = JSON.parse(event.data);
                                updateCachedData((draft)=>{
                                    // Normalize the `cards` array using the adapter
                                    const normalizedCards = cardsAdapter.upsertMany(cardsAdapter.getInitialState(), data.cards);
                                    // Normalize the `system_errors` array using the adapter
                                    const normalizedSystemErrors = systemErrorsAdapter.upsertMany(systemErrorsAdapter.getInitialState(), data.system_errors);
                                    // Normalize the `network` array using the adapter
                                    const normalizedNetworkMetrics = networkMetricsAdapter.upsertMany(networkMetricsAdapter.getInitialState(), data.network_metrics);
                                    // Normalize the `entropy_clients` array using the adapter
                                    const normalizedEntropyClients = entropyClientsAdapter.upsertMany(entropyClientsAdapter.getInitialState(), data.entropy_clients);
                                    draft["cards"] = normalizedCards;
                                    draft["timestamp"] = data.timestamp;
                                    draft["system_errors"] = normalizedSystemErrors;
                                    draft["entropy_stream_frozen"] = data.entropy_stream_frozen;
                                    draft["system_metrics"] = data.system_metrics;
                                    draft["network_metrics"] = normalizedNetworkMetrics;
                                    draft["utilization"] = data.utilization;
                                    draft["node_utilization"] = data.node_utilization;
                                    draft["entropy_clients"] = normalizedEntropyClients;
                                    draft["entropy_clients_by_node"] = data.entropy_clients_by_node ?? {};
                                });
                                // Reset the timeout since a message was received
                                resetMessageTimeout();
                            };
                            const onCpuModel = (event)=>{
                                const modelName = event.data;
                                updateCachedData((draft)=>{
                                    draft.cpu_model = modelName;
                                });
                            };
                            const onSystemError = (event)=>{
                                const data = JSON.parse(event.data);
                                const error = {
                                    timestamp: data.timestamp,
                                    error_msg: data.error_msg
                                };
                                // SSE error gets inserted into system errors section
                                updateCachedData((draft)=>{
                                    draft.system_errors = systemErrorsAdapter.upsertOne(systemErrorsAdapter.getInitialState(), error);
                                });
                            };
                            const onSeed = (event)=>{
                                const seedUpdate = JSON.parse(event.data);
                                dispatch(statusApi.util.updateQueryData("getHsms", undefined, (draft)=>{
                                    tokensAdapter.updateOne(draft.tokens, {
                                        id: seedUpdate.pkcs11Path + seedUpdate.serialNumber,
                                        changes: {
                                            lastSeedStatus: seedUpdate
                                        }
                                    });
                                }));
                            };
                            const connectSSE = ()=>{
                                // Close existing connection if any
                                if (eventSourceRef.current) {
                                    eventSourceRef.current.close();
                                }
                                eventSourceRef.current = createEventSource();
                                eventSourceRef.current.onopen = ()=>{
                                    connectionOpen = true;
                                    retryCount = 0; // Reset retry count on successful connection
                                    updateCachedData((draft)=>{
                                        draft.forbidden = false;
                                    });
                                    resetMessageTimeout();
                                    console.log("SSE connection established");
                                };
                                // Listen for status messages
                                eventSourceRef.current.addEventListener("data", listener);
                                eventSourceRef.current.addEventListener("cpu_model", onCpuModel);
                                eventSourceRef.current.addEventListener("system_error", onSystemError);
                                eventSourceRef.current.addEventListener("seed", onSeed);
                                // Listen for errors in opening SSE stream
                                eventSourceRef.current.addEventListener("error", async (e)=>{
                                    connectionOpen = false;
                                    // Handle non-retriable 403 forbidden error
                                    if (e.code === 403) {
                                        console.error("SSE connection forbidden (403)");
                                        updateCachedData((draft)=>{
                                            draft.forbidden = true;
                                        });
                                        return;
                                    }
                                    // Check if we've exceeded max retries
                                    if (retryCount >= MAX_RETRIES) {
                                        console.error(`SSE reconnect failed after ${MAX_RETRIES} attempts`);
                                        const error = {
                                            timestamp: Date.now(),
                                            error_msg: "Unable to connect to the backend service."
                                        };
                                        updateCachedData((draft)=>{
                                            draft.system_errors = systemErrorsAdapter.upsertOne(draft.system_errors, error);
                                        });
                                        return;
                                    }
                                    // Calculate exponential backoff delay
                                    const delay = Math.min(INITIAL_RETRY_DELAY * Math.pow(2, retryCount), MAX_RETRY_DELAY);
                                    retryCount++;
                                    console.error(`SSE connection error. Retry ${retryCount}/${MAX_RETRIES} in ${delay}ms...`);
                                    // Schedule reconnection
                                    reconnectTimeout = setTimeout(()=>{
                                        connectSSE();
                                    }, delay);
                                });
                            };
                            // Initial connection
                            connectSSE();
                        } catch  {}
                        await cacheEntryRemoved;
                        // Cleanup
                        if (messageTimeout) clearTimeout(messageTimeout);
                        if (reconnectTimeout) clearTimeout(reconnectTimeout);
                        if (eventSourceRef.current) {
                            eventSourceRef.current.close();
                        }
                    }
                }),
                getHsms: build.query({
                    query: ()=>({
                            url: "api/admin/qseed/info",
                            method: "GET"
                        }),
                    transformResponse: (response)=>{
                        let tokensState = tokensAdapter.getInitialState();
                        const serverStatus = response.serverStatus;
                        // Flatten tokens and merge HSM metadata into each token
                        const tokens = response.hsms.flatMap((hsm)=>hsm.tokens.map((token)=>({
                                    ...token,
                                    name: hsm.name,
                                    pkcs11Path: hsm.pkcs11Path,
                                    cryptokiVersion: hsm.cryptokiVersion,
                                    libraryDescription: hsm.libraryDescription,
                                    libraryVersion: hsm.libraryVersion
                                })));
                        // Normalize the tokens using the adapter and return the new state
                        tokensState = tokensAdapter.upsertMany(tokensState, tokens);
                        return {
                            serverStatus,
                            tokens: tokensState
                        };
                    },
                    providesTags: [
                        "Hsms"
                    ]
                }),
                updateHsm: build.mutation({
                    query: (payload)=>({
                            url: "api/admin/qseed/update",
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json"
                            },
                            body: payload
                        }),
                    // This tells RTK Query to re-fetch any queries that provide the 'Hsms' tag
                    invalidatesTags: [
                        "Hsms"
                    ]
                }),
                getCardDetails: build.query({
                    query: (cardId)=>({
                            url: `api/admin/card-details/${cardId}`,
                            method: "GET"
                        })
                }),
                getSoftwareVersion: build.query({
                    query: ()=>({
                            url: "api/admin/bundle-version",
                            method: "GET"
                        })
                }),
                getNodeConfig: build.query({
                    query: ()=>({
                            url: "api/admin/node-conf",
                            method: "GET"
                        })
                })
            })
    });
    return statusApi;
}
